import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { ComponentProps, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  BranchReference,
  BranchReferencesBase,
  updateBranchAddress,
} from "../types/branch-references-base";
import { AddressFieldsDebounced } from "../../addresses/components/address-fields-debounced";
import { BranchName } from "../../company/components/branch-name";

/**
 * BranchesReferenceBaseCard can be used to patch
 * the values saved with {@link BranchesReferenceBase}.
 * It is not intended to manage the references to service
 * objects itself (add / remove). Use {@link BranchReferencesBaseSelect}
 * for this.
 *
 * Use `hideSelect` if the service kind allows only one service object.
 */
export function BranchReferencesBaseCard({
  references,
  onReferencesChange,
  hideSelect = false,
  disabled = false,
  ...props
}: ComponentProps<typeof Card> & {
  references: BranchReferencesBase;
  onReferencesChange: (r: BranchReferencesBase) => void;
  disabled?: boolean;
  hideSelect?: boolean;
}) {
  const [selectedId, setSelectedId] = useState(
    references.branches[0]?.branchId,
  );

  const address = references.branches.find(
    (s) => s.branchId === selectedId,
  )?.branchAddress;

  const addressFieldName = `branches.${selectedId}.branchAddress`;

  return (
    <Card {...props}>
      <CardHeader>
        <CardTitle>{t("Niederlassungen")}</CardTitle>
        <CardDescription>
          {t("Adresse für die gewählte Niederlassung.")}
        </CardDescription>
        <CardAction>
          {!hideSelect && references.branches.length > 1 && (
            <ReferenceSelect
              value={selectedId}
              onValueChange={setSelectedId}
              options={references.branches}
            />
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {!selectedId && <div></div>}
        {address && (
          <AddressFieldsDebounced
            address={address}
            onAddressChange={(branchAddress) =>
              onReferencesChange(
                updateBranchAddress(references, selectedId, branchAddress),
              )
            }
            fieldName={addressFieldName}
            disabled={disabled}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ReferenceSelect({
  options,
  ...props
}: ComponentProps<typeof Select> & {
  options: BranchReference[];
}) {
  return (
    <Select {...props}>
      <SelectTrigger>
        <SelectValue placeholder={t("Bitte Object wählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((ref) => (
            <SelectItem value={ref.branchId} key={ref.branchId}>
              <BranchName branchId={ref.branchId} />
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
